import { Box, Card, HStack, SimpleGrid, useColorModeValue } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import PageLayout from '../../components/page-layout';
import { StaggeredGrid, StaggeredItem } from '../../components/animations/StaggeredGrid';
import { translate, TranslationKeys } from '../../i18n';
import { SportType } from '../../models/court/dto/court.dto';
import { useGetCourts } from '../court-management/hooks/useGetCourts';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { isDemoOrganization } from './demoAnalyticsData';
import AllCourtsUsageAsPercentageCard from './components/AllCourtsUsageAsPercentageCard';
import SportSelect from 'components/SportSelect';
import CumulativeBookingsChart from './components/CumulativeBookingsChart';
import CumulativeHoursBookedChart from './components/CumulativeHoursBookedChart';
import CumulativeRevenueChart from './components/CumulativeRevenueChart';
import CumulativeUserSignupsChart from './components/CumulativeUserSignupsChart';
import DateRangePicker from './components/DateRangePicker';
import EventCountCard from './components/EventCountCard';
import GameCountCard from './components/GameCountCard';
import GamesHeatmap from './components/GamesHeatmap';
import MultiCourtPicker from './components/MultiCourtPicker';
import RevenueCard from './components/RevenueCard';
import SubscriptionCountCard from './components/SubscriptionCountCard';
import TotalGameHoursCard from './components/TotalGameHoursCard';
import UserCountCard from './components/UserCountCard';
import GameTypeDistributionChart from './components/GameTypeDistributionChart';
import UserLevelsDistributionChart from './components/UserLevelsDistributionChart';


export type DateRangePickerIntervalType = 'All Time' | 'Last 24 Hours' | 'Last 7 Days' | 'Last 30 Days' | 'Last 90 Days' | 'Custom';

type PresetInterval = Exclude<DateRangePickerIntervalType, 'All Time' | 'Custom'>;

const INTERVAL_DURATIONS_MS: Record<PresetInterval, number> = {
    'Last 24 Hours': 1 * 24 * 60 * 60 * 1000,
    'Last 7 Days': 7 * 24 * 60 * 60 * 1000,
    'Last 30 Days': 30 * 24 * 60 * 60 * 1000,
    'Last 90 Days': 90 * 24 * 60 * 60 * 1000,
};

const GROWTH_LABEL_KEYS: Record<PresetInterval, TranslationKeys> = {
    'Last 24 Hours': 'analytics.vs_previous_24h',
    'Last 7 Days': 'analytics.vs_previous_7_days',
    'Last 30 Days': 'analytics.vs_previous_30_days',
    'Last 90 Days': 'analytics.vs_previous_90_days',
};

export default function AnalyticsPage() {
    const brandColor = useColorModeValue('brand.500', 'white');
    const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const { selectedOrganization } = useSelectedOrganization();
    const { selectedLocation } = useSelectedLocation();
    const isDemo = isDemoOrganization(selectedOrganization?.id);

    const [intervalType, setIntervalType] = useState<DateRangePickerIntervalType>('All Time');

    const [startDate, setStartDate] = useState(() => {
        const date = new Date()
        return new Date(date.getTime() - (30 * 24 * 60 * 60 * 1000))
    })
    const [endDate, setEndDate] = useState(() => {
        const date = new Date()
        return new Date(date.getTime() + (30 * 24 * 60 * 60 * 1000))
    });

    const [previousStartDate, setPreviousStartDate] = useState<Date | null>(null);
    const [previousEndDate, setPreviousEndDate] = useState<Date | null>(null);

    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const [selectedCourtIds, setSelectedCourtIds] = useState<number[]>([]);
    const [selectedSportTypes, setSelectedSportTypes] = useState<SportType[]>([]);

    const { data: courts } = useGetCourts(selectedLocation?.id);

    const availableSports = useMemo(() => {
        const set = new Set<SportType>();
        (courts ?? []).forEach(c => (c.sports ?? []).forEach(s => set.add(s)));
        return Array.from(set);
    }, [courts]);

    const growthLabel = useMemo(() => {
        if (intervalType === 'All Time' || intervalType === 'Custom') return undefined;
        return translate(GROWTH_LABEL_KEYS[intervalType]);
    }, [intervalType]);

    useEffect(() => {
        if (intervalType === 'Custom') {
            if (customStartDate && customEndDate) {
                const start = new Date(customStartDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(customEndDate);
                end.setHours(23, 59, 59, 999);
                setStartDate(start);
                setEndDate(end);
            }
            setPreviousStartDate(null);
            setPreviousEndDate(null);
            return;
        }

        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        switch (intervalType) {
            case 'All Time':
                setStartDate(new Date(0));
                setEndDate(new Date(new Date(0).getTime() + 100 * 365 * 24 * 60 * 60 * 1000));
                setPreviousStartDate(null);
                setPreviousEndDate(null);
                break;
            case 'Last 24 Hours':
            case 'Last 7 Days':
            case 'Last 30 Days':
            case 'Last 90 Days': {
                const durationMs = INTERVAL_DURATIONS_MS[intervalType];
                const now = new Date();
                const currentStart = new Date(now.getTime() - durationMs);
                setStartDate(currentStart);
                setEndDate(endDate);
                setPreviousStartDate(new Date(currentStart.getTime() - durationMs));
                setPreviousEndDate(new Date(currentStart.getTime()));
                break;
            }
        }
    }, [intervalType, customStartDate, customEndDate]);

    const analyticsFilters = useMemo(() => ({
        courtIds: selectedCourtIds.length > 0 ? selectedCourtIds : undefined,
        sportTypes: selectedSportTypes.length > 0 ? selectedSportTypes : undefined,
    }), [selectedCourtIds, selectedSportTypes]);

    return (
        <PageLayout>
            {/* Filters bar */}
            <Card p="16px">
                <HStack spacing='12px' flexWrap='wrap'>
                    <MultiCourtPicker
                        courts={courts ?? []}
                        selectedCourtIds={selectedCourtIds}
                        onChange={setSelectedCourtIds}
                    />
                    <SportSelect
                        mode="compact"
                        options={availableSports}
                        value={selectedSportTypes}
                        onChange={setSelectedSportTypes}
                        emptyLabel={translate('analytics.filters.all_sports')}
                        unitLabel={translate('analytics.filters.sports')}
                    />
                    <Box flex='1' />
                    <DateRangePicker
                        intervalType={intervalType}
                        setIntervalType={setIntervalType}
                        isDemo={isDemo}
                        customStartDate={customStartDate}
                        customEndDate={customEndDate}
                        onCustomStartDateChange={setCustomStartDate}
                        onCustomEndDateChange={setCustomEndDate}
                    />
                </HStack>
            </Card>

            {/* KPI stat cards with staggered entrance */}
            <StaggeredGrid staggerDelay={0.06}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 4 }} gap='20px'>
                    <StaggeredItem>
                        <UserCountCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <GameCountCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                            filters={analyticsFilters}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <TotalGameHoursCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                            filters={analyticsFilters}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <EventCountCard
                            brandColor={brandColor}
                            startDate={startDate}
                            endDate={endDate}
                            boxColor={boxBg}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                            filters={analyticsFilters}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <RevenueCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                            filters={analyticsFilters}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <SubscriptionCountCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <AllCourtsUsageAsPercentageCard boxColor={boxBg} brandColor={brandColor}
                            startDate={startDate} endDate={endDate}
                            previousStartDate={previousStartDate} previousEndDate={previousEndDate}
                            growthLabel={growthLabel}
                            filters={analyticsFilters}
                        />
                    </StaggeredItem>
                </SimpleGrid>
            </StaggeredGrid>

            {/* Heatmap */}
            <GamesHeatmap startDate={startDate} endDate={endDate} filters={analyticsFilters} />

            {/* Cumulative charts row 1 */}
            <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px'>
                <CumulativeUserSignupsChart />
                <CumulativeBookingsChart
                    filters={analyticsFilters}
                    startDate={intervalType !== 'All Time' ? startDate : undefined}
                    endDate={intervalType !== 'All Time' ? endDate : undefined}
                />
            </SimpleGrid>

            {/* Cumulative charts row 2 */}
            <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px'>
                <CumulativeRevenueChart
                    filters={analyticsFilters}
                    startDate={intervalType !== 'All Time' ? startDate : undefined}
                    endDate={intervalType !== 'All Time' ? endDate : undefined}
                />
                <CumulativeHoursBookedChart
                    filters={analyticsFilters}
                    startDate={intervalType !== 'All Time' ? startDate : undefined}
                    endDate={intervalType !== 'All Time' ? endDate : undefined}
                />
            </SimpleGrid>

            {/* Distribution charts */}
            <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px'>
                <GameTypeDistributionChart
                    startDate={startDate}
                    endDate={endDate}
                    filters={analyticsFilters}
                />
                <UserLevelsDistributionChart />
            </SimpleGrid>
        </PageLayout>
    );
}
